import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

import ListOfFilesLoadPopupComponent from './PopUpWindows/listOfFilesLoadPopupComponent';
import ListOfFilesDeletePopupComponent from './PopUpWindows/listOfFilesDeletePopupComponent';
import {
  //fetchToken,
  deleteReports,
  loadFilesActions,
  downloadFilePath,
  deleteSqliteFileTableContent,
} from './authorization/FetchToken';
import 'react-confirm-alert/src/react-confirm-alert.css';
import './index.css';
import Swal from 'sweetalert2';

const FileTree = ({ data, onItemSelected, firstFolderName }) => {
  // ──────────────────────────────────────────────
  // State Management
  // ──────────────────────────────────────────────
  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  const [schema, setSchema] = useState('');
  const [table, setTable] = useState('');
  const [chunk, setChunk] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileOrFolder, setFileOrFolder] = useState();

  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const [loadDatabaseList, setLoadDatabaseList] = useState([]);
  const [loadTableList, setLoadTableList] = useState([]);
  const [showDeleteSuccessMessage, setShowDeleteSuccessMessage] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!searchTerm.trim()) return;
  
    const expandAll = (node, result = {}) => {
      if (!node) return result;
  
      result[node.path] = true;
  
      node.children?.forEach((child) =>
        expandAll(child, result)
      );
  
      return result;
    };
  
    setExpandedFolders(expandAll(filteredData));
  }, [searchTerm]);



  const filterTree = (node, term) => {
      if (!node) return null;
    
      if (!term?.trim()) return node;
    
      const search = term.toLowerCase();
    
      const matches =
        node.info?.name?.toLowerCase().includes(search);
    
      if (!node.children || node.children.length === 0) {
        return matches ? node : null;
      }
    
      const filteredChildren = node.children
        .map((child) => filterTree(child, term))
        .filter(Boolean);
    
      if (matches || filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren,
        };
      }
    
      return null;
    };
  

  const filteredData = filterTree(data, searchTerm);

  // ──────────────────────────────────────────────
  // Folder / File Tree Handlers
  // ──────────────────────────────────────────────
  const toggleFolder = (path) =>
    setExpandedFolders((prev) => ({ ...prev, [path]: !prev[path] }));

  const handleItemClick = (item) => {
    setSelectedItem(item);
    if (onItemSelected) onItemSelected(item);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setIsDeletePopupOpen(false);
  };


  // ──────────────────────────────────────────────
  // Tree Renderer
  // ──────────────────────────────────────────────
  const renderTreeNode = (node, path = '', level = 0) => (
    <List
      disablePadding
      sx={{
        marginLeft: `${level * 16}px`,
        backgroundColor: 'transparent',
        borderLeft: level > 0 ? '1px dashed rgba(0,0,0,0.15)' : 'none',
        paddingLeft: level > 0 ? '12px' : '0',
      }}
    >
      {node?.map((item, index) => (
        <Box key={item.path || index}>
          <ListItem
            sx={{
              py: '4px',
              px: '8px',
              borderRadius: '6px',
              '&:hover': { backgroundColor: 'rgba(25,118,210,0.06)' },
              transition: 'background-color 0.15s ease',
            }}
          >
            {item.info.is_dir ? (
              <FolderNode node={item} path={item.path} level={level} />
            ) : (
              <FileNode node={item} />
            )}
          </ListItem>
          <Divider variant="inset" sx={{ borderColor: 'rgba(0,0,0,0.05)' }} />
        </Box>
      ))}
    </List>
  );

  // ──────────────────────────────────────────────
  // Folder Component
  // ──────────────────────────────────────────────
  const FolderNode = ({ node, path, level }) => {
  const isRoot = level === 0;

    return (
      <Box
        sx={{
          pl: `${level * 2}px`,
          cursor: 'pointer',
          color: selectedItem === node ? '#1976d2' : 'text.primary',
        }}
      >
        <Typography
          variant="body1"
          onClick={() => toggleFolder(path)}
          sx={{
            fontWeight: 600,
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {expandedFolders[path] ?  '<-' : '->'} {node.info.name}
        </Typography>

        {expandedFolders[path] && renderTreeNode(node.children, path, level + 1)}

        {!isRoot && node.info.name!=="data" && (
          <Box mt={0.5} ml={3} display="flex" gap={1}>


            <Tooltip title="Delete folder">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.preventDefault();
                  
                  Swal.fire({
                    title: 'Confirm Delete',
                    text: `Are you sure you want to delete "${node.info.name}"?`,
                    icon: 'warning',
                    background: '#ccc',
                    color: '#000',              // black message text
                    iconColor: '#d32f2f',
                    confirmButtonText: 'Delete',
                    confirmButtonColor: '#d32f2f',
                    cancelButtonText: 'Cancel',
                    cancelButtonColor: '#f5c518',  // yellow cancel
                    showCancelButton: true,
                  }).then((result) => {
                    if (result.isConfirmed)
                      handleDelete('invertible', 'connections', node.path);

                    
                  });

                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

          {!isRoot && (
          <Box mt={0.5} ml={3} display="flex" gap={1}>

            <Tooltip title="Download folder">
              <IconButton
                size="small"
                onClick={() => handleDownload(node)}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            </Box>
            )}


      </Box>
    );
  };

  // ──────────────────────────────────────────────
  // File Component
  // ──────────────────────────────────────────────
  const FileNode = ({ node }) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        ml: '30px',
        p: '6px 10px',
        borderRadius: '8px',
        backgroundColor:
          selectedItem === node ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
        transition: 'background-color 0.2s ease',
        cursor: 'pointer',
      }}
      onClick={() => handleItemClick(node)}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <InsertDriveFileIcon fontSize="small" color="primary" />
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            fontFamily: 'Inter, sans-serif',
            color: selectedItem === node ? '#1976d2' : 'text.primary',
          }}
        >
          <span>{node.info.name}</span>
          <span style={{ marginLeft: '2rem' }}>{node.info.mod_time.split('.')[0]}</span>
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        <Tooltip title="Download file">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(node);
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete file">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              Swal.fire({
                title: 'Confirm Delete',
                text: `Are you sure you want to delete "${node.info.name}"?`,
                icon: 'warning',
                background: '#ccc',
                color: '#000',              // black message text
                iconColor: '#d32f2f',
                confirmButtonText: 'Delete',
                confirmButtonColor: '#d32f2f',
                cancelButtonText: 'Cancel',
                cancelButtonColor: '#f5c518',  // yellow cancel
                showCancelButton: true,
              }).then((result) => {
                if (result.isConfirmed)
                  handleDelete('invertible', 'connections', node.path);
              });

            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  // ──────────────────────────────────────────────
  // Core Action Handlers
  // ──────────────────────────────────────────────
  const handleDelete = async (connectionName, stateType, fileName) => {
    setLoading(true);
    try {
      const fileObj = { name: fileName, type: '' };
      const response = await deleteReports(connectionName, fileObj, stateType,localStorage.getItem('orgName'), fileName);
      console.log('Delete Response:', response);
      setShowDeleteSuccessMessage(true);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (node) => {
    console.log('Downloading:', node);
    const pathSegments = node.path.split('\\');
    const folderIndex = pathSegments.indexOf(firstFolderName);
    let relativePath = '';

    if (folderIndex !== -1)
      relativePath = pathSegments.slice(folderIndex + 1).join('\\');

    if (node.info.is_dir)
      downloadObject(node.path, `${node.info.name}.zip`);
    else downloadObject(node.path, node.info.name);
  };

  const downloadObject = async (filePath, fileName) => {
    const data = await downloadFilePath(filePath);
    const blob = new Blob([data], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    URL.revokeObjectURL(url);
    document.body.removeChild(link);
  };

  const handleLoadSubmit = async (
    schema,
    table,
    fileFormatTypeValue,
    chunkSize,
    ifTableExists
  ) => {
    try {
      const response = await loadFilesActions(
        fileName,
        schema,
        table,
        fileFormatTypeValue,
        chunkSize,
        ifTableExists
      );

      if (response.code === 200) {
        setSuccessMessage('File Loaded Successfully');
        setIsPopupVisible(true);
      } else {
        setError(response.message);
        setShowErrorPopup(true);
      }
    } catch (error) {
      setError(error.message);
      setShowErrorPopup(true);
    }
  };

  const handleDeleteSubmit = async (onClose, schema, table, enterDelete) => {
    if (enterDelete?.toLowerCase() === 'delete') {
      try {
        const deleteContent = await deleteSqliteFileTableContent(
          schema,
          table,
          fileName,
          fileOrFolder
        );

        if (deleteContent?.Error && deleteContent.Code === 400) {
          setError(deleteContent.Error);
          setShowErrorPopup(true);
        } else {
          setSuccessMessage(
            fileOrFolder === 'file'
              ? 'File Deleted Successfully'
              : 'Folder Deleted Successfully'
          );
          setIsPopupVisible(true);
          onClose();
        }
      } catch (error) {
        setError(error.message);
        setShowErrorPopup(true);
      }
    } else {
      setError('Please enter DELETE to confirm.');
      setShowErrorPopup(true);
    }
  };

  // ──────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────
  return (
    
    <>
  <div style={{ marginBottom: "12px" }}>
    <input
      type="text"
      placeholder="Search files and folders..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      style={{
        width: "175px",
        padding: "8px 12px",
        border: "1px solid #ccc",
        borderRadius: "6px",
        fontSize: "14px"
      }}
    />
  </div>

  <div className="file-tree">
    {filteredData?.info && (
      <ul className="root-node">
        <li>
          <FolderNode
            node={filteredData}
            path={filteredData.path}
            level={0}
          />
        </li>
      </ul>
    )}

      {/* Load Popup */}
      {isPopupOpen && (
        <ListOfFilesLoadPopupComponent
          loadDatabaseList={loadDatabaseList}
          setLoadDatabaseList={setLoadDatabaseList}
          loadTableList={loadTableList}
          setLoadTableList={setLoadTableList}
          onClose={handleClosePopup}
          schema={schema}
          setSchema={setSchema}
          table={table}
          setTable={setTable}
          chunk={chunk}
          setChunk={setChunk}
          onValueSubmit={handleLoadSubmit}
        />
      )}

      {/* Delete Popup */}
      {isDeletePopupOpen && (
        <ListOfFilesDeletePopupComponent
          loadDatabaseList={loadDatabaseList}
          setLoadDatabaseList={setLoadDatabaseList}
          loadTableList={loadTableList}
          setLoadTableList={setLoadTableList}
          onClose={handleClosePopup}
          schema={schema}
          setSchema={setSchema}
          table={table}
          setTable={setTable}
          chunk={chunk}
          setChunk={setChunk}
          onValueSubmit={handleDeleteSubmit}
        />
      )}

      {/* Success Popup */}
      {isPopupVisible && (
        <div className="overlay">
          <div className="success-popup-window">
            <div className="success-message">{successMessage}</div>
            <button className="close-button" onClick={() => setIsPopupVisible(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && (
        <div className="overlay">
          <div className="errorpopup-window">
            <div className="error-message">{error}</div>
            <button className="close-button" onClick={() => setShowErrorPopup(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  </>
);
};

export default FileTree;
